import React, { useState, useEffect, Component } from 'react';
import { useExpense } from '../../context/ExpenseContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Skeleton } from '../ui/skeleton';
import { format, parseISO } from 'date-fns';
import { 
  ClipboardList, LogIn, LogOut, Plus, Pencil, Trash2, 
  Upload, Download, RefreshCw, UserPlus, Key, AlertTriangle
} from 'lucide-react';

// Error Boundary to catch rendering errors
class AuditLogsErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('AuditLogs Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="space-y-6" data-testid="expense-audit-logs-error">
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-red-600 mb-2">Something went wrong</h3>
                <p className="text-muted-foreground mb-4">Unable to load audit logs</p>
                <button 
                  onClick={() => this.setState({ hasError: false, error: null })}
                  className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90"
                >
                  Try Again
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }
    return this.props.children;
  }
}

const ACTION_ICONS = {
  'CREATE': Plus,
  'UPDATE': Pencil,
  'DELETE': Trash2,
  'DUPLICATE': RefreshCw,
  'IMPORT': Upload,
  'EXPORT': Download,
  'BULK_DELETE_ALL': Trash2,
  'BULK_DELETE_RANGE': Trash2,
  'LOGIN': LogIn,
  'LOGOUT': LogOut,
  'BACKUP': Download,
  'RESTORE': Upload,
  'ADD_LOV': Plus,
  'REMOVE_LOV': Trash2,
  'USER_CREATE': UserPlus,
  'PASSWORD_RESET': Key
};

const ACTION_COLORS = {
  'CREATE': 'bg-green-100 text-green-700',
  'UPDATE': 'bg-blue-100 text-blue-700',
  'DELETE': 'bg-red-100 text-red-700',
  'DUPLICATE': 'bg-purple-100 text-purple-700',
  'IMPORT': 'bg-yellow-100 text-yellow-700',
  'EXPORT': 'bg-cyan-100 text-cyan-700',
  'BULK_DELETE_ALL': 'bg-red-100 text-red-700',
  'BULK_DELETE_RANGE': 'bg-red-100 text-red-700',
  'LOGIN': 'bg-green-100 text-green-700',
  'LOGOUT': 'bg-slate-100 text-slate-700',
  'BACKUP': 'bg-blue-100 text-blue-700',
  'RESTORE': 'bg-orange-100 text-orange-700',
  'ADD_LOV': 'bg-green-100 text-green-700',
  'REMOVE_LOV': 'bg-red-100 text-red-700',
  'USER_CREATE': 'bg-green-100 text-green-700',
  'PASSWORD_RESET': 'bg-yellow-100 text-yellow-700'
};

const ExpenseAuditLogs = () => {
  const { getAuditLogs } = useExpense();
  
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [pagination, setPagination] = useState({ page: 1, page_size: 50, total: 0, total_pages: 0 });

  useEffect(() => {
    loadLogs(1);
  }, [filter]);

  const loadLogs = async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const action = filter === 'all' ? null : filter;
      const response = await getAuditLogs(page, 50, action);
      
      // Handle paginated response format
      if (response && Array.isArray(response.data)) {
        setLogs(response.data);
        setPagination(response.pagination || { page: 1, page_size: 50, total: response.data.length, total_pages: 1 });
      } else if (Array.isArray(response)) {
        // Fallback for old format (direct array)
        setLogs(response);
        setPagination({ page: 1, page_size: response.length, total: response.length, total_pages: 1 });
      } else {
        // Response is neither paginated nor array - set empty
        console.warn('Unexpected audit logs response format:', response);
        setLogs([]);
        setPagination({ page: 1, page_size: 50, total: 0, total_pages: 0 });
      }
    } catch (err) {
      console.error('Failed to load audit logs:', err);
      setError(err.message || 'Failed to load audit logs');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    loadLogs(newPage);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      return format(parseISO(timestamp), 'PPpp');
    } catch {
      return timestamp;
    }
  };

  const getActionIcon = (action) => {
    const Icon = ACTION_ICONS[action] || ClipboardList;
    return <Icon className="w-4 h-4" />;
  };

  const getActionBadgeClass = (action) => {
    return ACTION_COLORS[action] || 'bg-slate-100 text-slate-700';
  };

  // Static list of all available actions for the filter dropdown
  // This prevents the "derived state" bug where options disappear when filtering
  const ALL_ACTIONS = [
    'CREATE', 'UPDATE', 'DELETE', 'DUPLICATE', 
    'IMPORT', 'EXPORT', 'BULK_DELETE_ALL', 'BULK_DELETE_RANGE',
    'BACKUP', 'RESTORE', 'ADD_LOV', 'REMOVE_LOV'
  ];

  if (error) {
    return (
      <div className="space-y-6" data-testid="expense-audit-logs">
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-red-500">
              <p>Error loading audit logs: {error}</p>
              <button 
                onClick={() => loadLogs(1)} 
                className="mt-4 px-4 py-2 bg-primary text-white rounded hover:bg-primary/90"
              >
                Retry
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="expense-audit-logs">
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ClipboardList className="w-5 h-5" />
                Audit Logs
              </CardTitle>
              <CardDescription>
                Immutable record of all system actions
              </CardDescription>
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Filter:</span>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  {ALL_ACTIONS.map(action => (
                    <SelectItem key={action} value={action}>{action}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map(i => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : !Array.isArray(logs) || logs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No audit logs found
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[180px]">Timestamp</TableHead>
                    <TableHead className="w-[120px]">User</TableHead>
                    <TableHead className="w-[140px]">Action</TableHead>
                    <TableHead className="w-[100px]">Entity</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log, index) => (
                    <TableRow key={log?.id || index}>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {formatTimestamp(log?.timestamp)}
                      </TableCell>
                      <TableCell className="font-medium">
                        {log?.username || 'Unknown'}
                      </TableCell>
                      <TableCell>
                        <Badge className={`gap-1 ${getActionBadgeClass(log?.action)}`}>
                          {getActionIcon(log?.action)}
                          {log?.action || 'UNKNOWN'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{log?.entity_type || 'N/A'}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[300px] truncate">
                        {log?.details || ''}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          
          <div className="flex items-center justify-between mt-4 text-sm text-muted-foreground">
            <span>Showing {Array.isArray(logs) ? logs.length : 0} of {pagination.total} entries</span>
            {pagination.total_pages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted"
                >
                  Previous
                </button>
                <span>Page {pagination.page} of {pagination.total_pages}</span>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.total_pages}
                  className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted"
                >
                  Next
                </button>
              </div>
            )}
            <span>Logs are immutable and cannot be modified</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Wrap with error boundary for safety
const ExpenseAuditLogsWithErrorBoundary = () => (
  <AuditLogsErrorBoundary>
    <ExpenseAuditLogs />
  </AuditLogsErrorBoundary>
);

export default ExpenseAuditLogsWithErrorBoundary;
